import type { CatalogProduct } from '../../types'
import type { ProductCustomFieldRow } from './helpers'
import { isHttpUrl } from './helpers'
import { resolveRendererForField } from './field-renderers/registry'
import type { ShopLocale } from '../../../i18n/shopLocale'
import { ProductFieldRuntimeSlot } from './field-renderers/runtime-slot'

const renderScalarValue = (value: unknown) => {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  return String(value)
}

const renderValueWithUnit = (row: ProductCustomFieldRow) => {
  const rendered = renderScalarValue(row.value)
  const unit = row.field.unit?.trim()
  if (!unit || !rendered.trim() || Array.isArray(row.value) || typeof row.value === 'boolean') return rendered
  return `${rendered} ${unit}`
}

export const renderProductFieldFallbackNode = (row: ProductCustomFieldRow) => {
  const { field, value } = row

  if (Array.isArray(value)) {
    return (
      <ul className="list-disc space-y-1 pl-4">
        {value.map((item) => (
          <li key={`${field.key}-${item}`} className="text-slate-700">
            {renderScalarValue(item)}
          </li>
        ))}
      </ul>
    )
  }

  if (typeof value === 'string' && isHttpUrl(value)) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="break-all text-cyan-700 underline decoration-cyan-300 underline-offset-2 hover:text-cyan-800"
      >
        {value}
      </a>
    )
  }

  return <>{renderValueWithUnit(row)}</>
}

export const ProductFieldValue = ({
  row,
  productData,
  locale,
}: {
  row: ProductCustomFieldRow
  productData?: CatalogProduct | null
  locale?: ShopLocale
}) => {
  const { field, value } = row
  const renderer = resolveRendererForField(field)

  if (renderer) {
    const RendererComponent = renderer.component
    return <RendererComponent field={field} value={value} className="mt-0.5" />
  }

  return (
    <ProductFieldRuntimeSlot
      field={field}
      value={value}
      productData={productData ?? null}
      locale={locale}
      fallback={renderProductFieldFallbackNode(row)}
      className="mt-0.5"
    />
  )
}

export const renderProductFieldInlineText = (row: ProductCustomFieldRow): string => {
  const { value } = row
  if (Array.isArray(value)) return value.map((entry) => String(entry)).join(', ')
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  return renderValueWithUnit(row)
}
